import produce from 'immer';
import * as _ from 'lodash';
import type { AnyAction } from 'redux';
import type { ThunkAction } from 'redux-thunk';

import { Category, ENDPOINTS, ISSUE_TYPE_SORT, SEVERITY_SORT } from '../../../shared/issuesV2';
import * as promises from '../../../shared/promises';
import { window } from '../../globals';
import { ISSUE_CATEGORY_SUMMARY_QUERY_KEY_PREFIX } from '../../hooks/api/issues/useIssueCategorySummary';
import queryClient from '../../ReactQuery/client';
import { nuc } from '../../utils';
import { remove } from '../api';
import CommentActions from '../comments/actions';
import OrganizationsStore from '../organizations';
import { setCurrentFilter } from '../savedFilters/sharedActions';
import TeamsModule from '../teams';
import type { CveDataDisputeReasonsType } from '../../../shared/cveDisputeTypes';
import type {
  Action,
  AnyExportRequest,
  AnySort,
  APIIssuesActionQuery,
  APIIssuesQuery,
  APIIssueSummaryQuery,
  CategoriedIssuesFilterQuery,
  CategoriedIssuesQuery,
  ChangeStatus,
  ExceptionAction,
  ExceptionIgnoreScope,
  ExceptionPackageScope,
  ExportAction,
  FilterByCategory,
  FoundAfterOption,
  Grouping,
  IgnoreAction,
  IssueExceptionOrderBy,
  IssueExceptionsFilter,
  IssueExceptionsForDashboard,
  IssueExceptionSort,
  IssueExceptionsPagination,
  IssueExceptionsRemoveRequest,
  IssueExceptionsRemoveResponse,
  IssuePackageManagersResponse,
  IssueRevisionsResponse,
  IssuesActionResponse,
  IssuesCategorySummary,
  IssuesQuery,
  IssuesResponse,
  IssuesStatusSummary,
  IssueTypesSummary,
  QualityIssueType,
  Scope,
  Status,
  UnignoreAction,
  UnlinkAction,
  VulnDisputeRequest,
  VulnIgnoreReason,
} from '../../../shared/issuesV2';
import type { ProjectType } from '../../../typings/enums';
import type { AwaitedThunkResponse, ReduxAction, SyncReduxAction, TeamSelection } from '../../types';
import type { ReduxStore } from '../store';

import {
  getFetchIssuesQuery,
  getGrouping,
  getIssueCategory,
  getIssueCommentsProjectId,
  getIssueDetails,
  getIssuesFilterQuery,
  getIssuesSummaryQuery,
  getRevisions,
  getScope,
  getSelectedIssueIds,
  getSelectedRevisionIds,
} from './selectors';
import { get, post, put } from './utils';
import type { HydrateStateQuery, IssueRevisionWithIssues, RevisionLicensesResponse } from './types';

export const actionTypes = Object.freeze({
  RESET: 'ISSUESV2_RESET',
  SET_FILTER: 'ISSUESV2_SET_FILTER',
  SET_FOUND_AFTER_FILTER: 'ISSUESV2_SET_FOUND_AFTER_FILTER',
  SET_SCOPE: 'ISSUESV2_SET_SCOPE',
  SET_SCOPE_SCAN: 'ISSUESV2_SET_SCOPE_SCAN',
  SET_STATUS: 'ISSUESV2_SET_STATUS',
  SET_ISSUE_CATEGORY: 'ISSUESV2_SET_ISSUE_CATEGORY',
  PRE_FETCH_LOADING: 'ISSUESV2_PREFETCH_LOADING',
  SET_SEARCH: 'ISSUESV2_SET_SEARCH',
  SET_SORT: 'ISSUESV2_SET_SORT',
  SET_PAGE: 'ISSUESV2_SET_PAGE',
  SET_COUNT: 'ISSUESV2_SET_COUNT',
  SET_GROUPING: 'ISSUESV2_SET_GROUPING',
  SET_ITEMS: 'ISSUESV2_SET_ITEMS',
  SELECT_ISSUE: 'ISSUESV2_SELECT_ISSUE',
  SELECT_REVISION: 'ISSUESV2_SELECT_REVISION',
  DESELECT_ISSUE: 'ISSUESV2_DESELECT_ISSUE',
  TOGGLE_SELECT_ALL_ON_PAGE: 'ISSUESV2_TOGGLE_SELECT_ALL_ON_PAGE',
  SELECT_ALL_ISSUES: 'ISSUESV2_SELECT_ALL_ISSUES',
  CLEAR_SELECTION: 'ISSUESV2_CLEAR_SELECTION',
  HYDRATE_STATE: 'ISSUESV2_HYDRATE_STATE',
  FETCH_CATEGORY_SUMMARY: 'ISSUESV2_FETCH_CATEGORY_SUMMARY',
  FETCH_STATUS_SUMMARY: 'ISSUESV2_FETCH_STATUS_SUMMARY',
  FETCH_ISSUES: 'ISSUESV2_FETCH_ISSUES',
  FETCH_REVISIONS: 'ISSUESV2_FETCH_REVISIONS',
  FETCH_REVISION_ISSUES: 'ISSUESV2_FETCH_REVISION_ISSUES',
  FETCH_ISSUE_DETAILS: 'ISSUESV2_FETCH_ISSUE_DETAILS',
  FETCH_ISSUE_LICENSES: 'ISSUESV2_FETCH_ISSUE_LICENSES',
  FETCH_AFFECTED_PROJECTS: 'ISSUESV2_FETCH_AFFECTED_PROJECTS',
  RESET_FILTERS: 'ISSUESV2_RESET_FILTERS',
  UPDATE_ISSUES: 'ISSUESV2_UDPATE_ISSUES',
  SET_PROJECT_TYPE: 'SET_PROJECT_TYPE',
  CLEAR_UPDATE_ERRORS: 'CLEAR_UPDATE_ERRORS',
  FETCH_ISSUE_EXCEPTIONS: 'FETCH_ISSUE_EXCEPTIONS',
  REMOVE_ISSUE_EXCEPTIONS: 'REMOVE_ISSUE_EXCEPTIONS',
  CLEAR_ISSUE_DETAILS: 'ISSUESV2_CLEAR_ISSUE_DETAILS',
  POST_VULN_DISPUTE: 'ISSUESV2_POST_VULN_DISPUTE',
  EXPAND_ISSUE_REVISION: 'ISSUESV2_EXPAND_ISSUE_REVISION',
  EXPAND_ALL_ISSUE_REVISIONS: 'ISSUESV2_EXPAND_ALL_ISSUE_REVISIONS',
  COLLAPSE_ISSUE_REVISION: 'ISSUESV2_COLLAPSE_ISSUE_REVISION',
  COLLAPSE_ALL_ISSUE_REVISIONS: 'ISSUESV2_COLLAPSE_ALL_ISSUE_REVISIONS',
  FETCH_ISSUE_TYPE_SUMMARY: 'ISSUESV2_FETCH_ISSUE_TYPE_SUMMARY',
  FETCH_PACKAGE_MANAGERS: 'ISSUESV2_FETCH_PACKAGE_MANAGERS',
  FETCH_CWES: 'ISSUESV2_FETCH_CWES',
  SET_PACKAGE_MANAGERS_FILTER: 'ISSUESV2_SET_PACKAGE_MANAGERS_FILTER',
  SET_CWES_FILTER: 'ISSUESV2_SET_CWES_FILTER',
  SET_PROJECT_LABELS_FILTER: 'ISSUESV2_SET_PROJECT_LABELS_FILTER',
  FETCH_LICENSES_LIST: 'ISSUESV2_FETCH_LICENSES_LIST',
  EXTEND_ISSUE_EXCEPTION: 'ISSUESV2_EXTEND_ISSUE_EXCEPTION',

  // Issue Diffs
  ENTER_COMPARISON_MODE: 'ISSUESV2_ENTER_COMPARISON_MODE',
  EXIT_COMPARISON_MODE: 'ISSUESV2_EXIT_COMPARISON_MODE',
  SET_COMPARISON_REVISION: 'ISSUESV2_SET_COMPARISON_REVISION',
  SET_COMPARISON_RELEASE: 'ISSUESV2_SET_COMPARISON_RELEASE',
  SET_COMPARISON_CHANGE_STATUS: 'ISSUESV2_SET_COMPARISON_CHANGE_STATUS',
});

type IssuesThunkAction = ThunkAction<void, ReduxStore, never, AnyAction>;

/**
 * Whenever we unmount the IssuesContainer (ie: the user has left the issues page),
 * we want to reset everything so that we don't see stale data if we (for example)
 * navigate from the global issues page to the project issues page.
 */
export const resetState = () => ({
  type: actionTypes.RESET,
});

/**
 * Action for updating the "scope" (ie: global, project or release group).
 * This should happen immediately when the page route changes/page mounts,
 * so that all requests afterwards are using the correct scope.
 */
export const setScope = (scope: Scope): SyncReduxAction<{ scope: Scope }> => ({
  type: actionTypes.SET_SCOPE,
  scope,
});

/**
 * Action for updating the "scope" (ie: global, project or release group).
 * This should happen immediately when the page route changes/page mounts,
 * so that all requests afterwards are using the correct scope.
 */
export const setScanId = (scanId?: number): SyncReduxAction<{ scanId?: number }> => ({
  type: actionTypes.SET_SCOPE_SCAN,
  scanId,
});

/**
 * Action for updating the "projectType".
 * This should happen immediately when the page route changes/page mounts,
 * so that all requests afterwards are using the correct project type.
 */
export const setProjectType = (projectType?: ProjectType): SyncReduxAction<{ projectType?: ProjectType }> => ({
  type: actionTypes.SET_PROJECT_TYPE,
  projectType,
});

/**
 * For cases where users land directly on the IssueDetails page, we need to make
 * sure that the category is correctly set so the correct nav item is "active".
 * In IssuesInbox, this already happens with the hydrateState action.
 */
export const setIssueCategory = (issueCategory: Category): SyncReduxAction<{ issueCategory: Category }> => ({
  type: actionTypes.SET_ISSUE_CATEGORY,
  issueCategory,
});

/**
 * Action for updating the status, when changing between the various
 * issue "status" tabs in IssueStatusTabs.
 */
export const setStatus = (status: Status): SyncReduxAction<{ status: Status }> => ({
  type: actionTypes.SET_STATUS,
  status,
});

/**
 * This action informs the UI that a user-action has taken place,
 * and that a request is about to happen (it waits for the debounce timeout).
 * Basically the same as `ISSUESV2_FETCH_ISSUES_PENDING`, but separated to avoid confusion.
 */
export const preFetchLoading = () => ({
  type: actionTypes.PRE_FETCH_LOADING,
});

/**
 * Action for updating all filters
 */
export const setFilter = <C extends Category>(
  filter?: FilterByCategory<C>,
): SyncReduxAction<{ filter?: FilterByCategory<C> }> => ({
    type: actionTypes.SET_FILTER,
    filter,
  });

/**
 * Updates the searchbar state (IssuesSearch.tsx)
 */
export const setSearch = (search: string): SyncReduxAction<{ search: string }> => ({
  type: actionTypes.SET_SEARCH,
  search,
});

/**
 * Updates the sort (IssuesSortingDropdown.tsx)
 */
export const setSort = (sort: AnySort): SyncReduxAction<{ sort: AnySort }> => ({
  type: actionTypes.SET_SORT,
  sort,
});

/**
 * Updates the grouping (IssuesGroupingDropdown.tsx)
 */
export const setGrouping = (grouping: Grouping): SyncReduxAction<{ grouping: Grouping }> => ({
  type: actionTypes.SET_GROUPING,
  grouping,
});

/**
 * Resets the filters and the search field
 */
export const resetAllFilters = () => ({
  type: actionTypes.RESET_FILTERS,
});

/**
 * Action for updating the pagination page.
 */
export const setPage = (page: number): SyncReduxAction<{ page: number }> => ({
  type: actionTypes.SET_PAGE,
  page,
});

/**
 * Action for updating the pagination count.
 */
export const setCount = (count: number): SyncReduxAction<{ count: number }> => ({
  type: actionTypes.SET_COUNT,
  count,
});

/**
 * Action for selecting an issue.
 */
export const selectIssue = (issueId: number): SyncReduxAction<{ issueId: number }> => ({
  type: actionTypes.SELECT_ISSUE,
  issueId,
});

/**
 * Action for deselecting an issue.
 */
export const deselectIssue = (issueId: number): SyncReduxAction<{ issueId: number }> => ({
  type: actionTypes.DESELECT_ISSUE,
  issueId,
});

/**
 * Action for selecting a revision.
 */
export const toggleSelectRevision = (revisionId: string): SyncReduxAction<{ revisionId: string }> => ({
  type: actionTypes.SELECT_REVISION,
  revisionId,
});

/**
 * Action for toggling the bulk selection checkbox.
 */
export const toggleSelectAllOnPage = (): AnyAction => ({
  type: actionTypes.TOGGLE_SELECT_ALL_ON_PAGE,
});

/**
 * Action for setting selectedIssues to 'all', across all pages.
 */
export const selectAllIssues = (): AnyAction => ({
  type: actionTypes.SELECT_ALL_ISSUES,
});

/**
 * Action for clearing the selection in the IssuesBulkSelectionBanner
 */
export const clearSelection = (): AnyAction => ({
  type: actionTypes.CLEAR_SELECTION,
});

/**
 * Used to clear out `asyncStatus.updateIssues.errors` after we close an action modal.
 * This prevents stale errors from showing up again if the user opens the modal again,
 * or opens a different modal.
 */
export const clearUpdateErrors = (): AnyAction => ({
  type: actionTypes.CLEAR_UPDATE_ERRORS,
});

/**
 * Handles hydrating the state based on route changes + URL
 * query params when the page initially loads.
 *
 * @param {HydrateStateQuery} query - A partial state, to be hydrated.
 */
export const hydrateStateAction = (query: HydrateStateQuery): SyncReduxAction<{ query: HydrateStateQuery }> => ({
  type: actionTypes.HYDRATE_STATE,
  query,
});

//  Handles hydrating the state, as well as setting the savedFilters.currentFilter if it exists.
export const hydrateState =
  (query: HydrateStateQuery): IssuesThunkAction =>
    (dispatch) => {
      dispatch(hydrateStateAction(query));
      if (query.savedFilter) {
        dispatch(setCurrentFilter(query.savedFilter));
      }
    };

/**
 * Action dispatched by the new IssuesInboxSubnav,
 * returns the counts for each issue category.
 * Note that the scope needs to be set before this is dispatched
 */
export const fetchCategorySummary = (scope: Scope, teamId?: TeamSelection): ReduxAction<IssuesCategorySummary> => ({
  type: actionTypes.FETCH_CATEGORY_SUMMARY,
  meta: { timestamp: Date.now() },
  payload: async () => get<IssuesCategorySummary>(ENDPOINTS.CATEGORY_SUMMARY, { scope, teamId }),
});

/**
 * Handles fetching the status summary, which is handled + displayed
 * in the IssueStatusTabs component.
 */
const fetchStatusSummaryAction = <C extends Category>(
  query: APIIssueSummaryQuery<C>,
): ReduxAction<IssuesStatusSummary> => ({
    type: actionTypes.FETCH_STATUS_SUMMARY,
    meta: { timestamp: Date.now() },
    payload: async () => get<IssuesStatusSummary>(ENDPOINTS.STATUS_SUMMARY, query),
  });

//  Async/thunk action, which can grab the state and provide it to the action.
export const fetchStatusSummary = (): IssuesThunkAction => (dispatch, getState) => {
  //  Get the latest copy of the state, and use the selector to grab just the issuesV2 store.
  const state = getState();
  const query = getIssuesSummaryQuery(state);

  //  Dispatch the actual async action which makes the request.
  dispatch(fetchStatusSummaryAction(query));
};

/**
 * Action creator for the fetchIssues action.
 * Note that the fetchIssues thunk below is what handles passing the
 * category and query to this action, so this action isn't dispatched directly.
 */
const fetchIssuesAction = <C extends Category>(query: APIIssuesQuery<C>) => ({
  type: actionTypes.FETCH_ISSUES,
  meta: { timestamp: Date.now() },
  payload: async () => get<IssuesResponse<C>>(ENDPOINTS.FETCH_ISSUES, query),
});

/**
 * Action creator for the fetchIssueLicenses action.
 * Note that the fetchIssueLicenses thunk below is what handles passing the
 * category and query to this action, so this action isn't dispatched directly.
 */
const fetchIssueLicenseListAction = <C extends Category>(query: APIIssuesQuery<C>) => ({
  type: actionTypes.FETCH_LICENSES_LIST,
  meta: { timestamp: Date.now() },
  payload: async () => get<IssuesResponse<C>>(ENDPOINTS.LICENSE_LIST, query),
});

/**
 * Action creator for the fetchRevisions action.
 */
const fetchRevisionsAction = <C extends Category>(query: APIIssuesQuery<C>) => ({
  type: actionTypes.FETCH_REVISIONS,
  meta: { timestamp: Date.now() },
  payload: async () => get<IssueRevisionsResponse>(ENDPOINTS.FETCH_REVISIONS, query),
});

/**
 * Action creator for fetching issues related to a specific revision.
 */
const fetchRevisionIssuesAction = <C extends Category>(query: APIIssuesQuery<C>) => ({
  type: actionTypes.FETCH_REVISION_ISSUES,
  meta: { timestamp: Date.now(), revisionIds: (query as CategoriedIssuesQuery<C>).filter?.revisionIds },
  payload: async () => get<IssuesResponse<C>>(ENDPOINTS.FETCH_ISSUES, query),
});

/**
 * This thunk is responsible for taking the current state and massaging
 * it into the fetchIssues query. This is dispatched from the useFetchIssuesAction
 * hook, which is responsible for the debouncing of this action.
 */
export const fetchIssues = (): IssuesThunkAction => (dispatch, getState) => {
  //  Get the latest copy of the state, and use the selector to grab just the issuesV2 store.
  const state = getState();

  if (getGrouping(state) === 'issue') {
    dispatch(fetchIssuesAction(getFetchIssuesQuery(state)));
  }

  if (getGrouping(state) === 'revision') {
    dispatch(fetchRevisionsAction(getFetchIssuesQuery(state)));
  }
};

/**
 * This thunk is responsible for taking the current state and massaging
 * it into the fetchIssues query. This is dispatched from the useFetchIssuesAction
 * hook, which is responsible for the debouncing of this action.
 */
export const fetchIssueLicenseList = (): IssuesThunkAction => (dispatch, getState) => {
  //  Get the latest copy of the state, and use the selector to grab just the issuesV2 store.
  const state = getState();
  if (getIssueCategory(state) === Category.Licensing) {
    dispatch(fetchIssueLicenseListAction(getFetchIssuesQuery(state)));
  }
};

/**
 * Async action for updating issues, regardless of action or number of issues being updated.
 * The thunk actions below handle determining which type of query to pass here,
 * as well as reading from the state to construct that query.
 */
const updateIssues = <C extends Category>(
  query: APIIssuesActionQuery<C>,
  action: Action,
) => ({
    type: actionTypes.UPDATE_ISSUES,
    payload: async () => put<Action, IssuesActionResponse>(ENDPOINTS.UPDATE_ISSUES, action, { query }),
  });

/** The target of an action can be everything, or lists of issues and revisions. */
export type ActionTarget = 'all' | { issueIds?: number[]; revisionIds?: string[] };

//  Thunk which handles determining the update actions query based on the current state, then dispatching updateIssues.
const updateIssuesThunk =
  <C extends Category>(target: ActionTarget, action: Action): IssuesThunkAction =>
    (dispatch, getState) => {
      const state = getState();
      const filterQuery = getIssuesFilterQuery(state);

      //  Handle case where we want to do an action against all selected issues
      if (target === 'all') {
        return dispatch(updateIssues(filterQuery, action));
      }

      // When passing revisions and issues the result is a combination
      // TODO: Clean up the type some more.
      const query = produce(filterQuery, (draft) => {
        draft.ids = target.issueIds;
        draft.filter ??= {};
        draft.filter.revisionIds = target.revisionIds;
      }) as CategoriedIssuesFilterQuery<C>;

      return dispatch(updateIssues(query, action));
    };

/** Gets the ActionTarget for the current selection */
const getTargetForSelection = (state: ReduxStore): ActionTarget => {
  const issueIds = getSelectedIssueIds(state);
  const revisionIds = getSelectedRevisionIds(state);
  return issueIds === 'all' || revisionIds === 'all' ? 'all' : { issueIds, revisionIds };
};

/**
 * Takes in an action that requires the selection target as the first parameter
 * and returns an action that is identical but gets that target from the current
 * selection instead of from a parameter.
 */
const withTargetFromSelection =
  <Args extends unknown[]>(action: (target: ActionTarget, ...args: Args) => IssuesThunkAction) =>
    (...args: Args): IssuesThunkAction =>
      (dispatch, getState, extraArg) =>
        action(getTargetForSelection(getState()), ...args)(dispatch, getState, extraArg);

const getIgnoreAction = (notes?: string, reason?: VulnIgnoreReason): IgnoreAction => ({
  type: 'ignore',
  notes,
  reason,
});

const getIssueExceptionAction = (
  ignoreScope: ExceptionIgnoreScope,
  packageScope: ExceptionPackageScope,
  expiresAfter: string | null,
  notes?: string,
  reason?: VulnIgnoreReason,
  packageLabelIds?: number[],
  licenseId?: string,
  qualityIssueType?: QualityIssueType,
): ExceptionAction => ({
  type: 'issueException',
  notes,
  reason,
  ignoreScope,
  packageScope,
  expiresAfter,
  packageLabelIds,
  licenseId,
  qualityIssueType,
});

/** Ignores issues given by the target. */
export const ignoreIssues =
  (target: ActionTarget, notes?: string, reason?: VulnIgnoreReason): IssuesThunkAction =>
    async (dispatch) => {
      await dispatch(updateIssuesThunk(target, getIgnoreAction(notes, reason)));
      dispatch(refetchSummaries());
    };

/** Ignores the currently selected issues. */
export const ignoreSelectedIssues = withTargetFromSelection(ignoreIssues);

const getUnignoreAction = _.constant<UnignoreAction>({ type: 'unignore' });

/** Unignores issues given by the target. */
export const unignoreIssues =
  (target: ActionTarget): IssuesThunkAction =>
    async (dispatch) => {
      await dispatch(updateIssuesThunk(target, getUnignoreAction()));
      dispatch(refetchSummaries());
    };

/** Unignores the currently selected issues. */
export const unignoreSelectedIssues = withTargetFromSelection(unignoreIssues);

/** Creates issue exceptions for the target issues. */
export const createIssueExceptions =
  (
    target: ActionTarget,
    ignoreScope: ExceptionIgnoreScope,
    packageScope: ExceptionPackageScope,
    expiresAfter: string | null,
    notes?: string,
    reason?: VulnIgnoreReason,
    packageLabels?: number[],
    licenseId?: string,
    qualityIssueType?: QualityIssueType,
  ): IssuesThunkAction =>
    async (dispatch) => {
      await dispatch(
        updateIssuesThunk(
          target,
          getIssueExceptionAction(
            ignoreScope,
            packageScope,
            expiresAfter,
            notes,
            reason,
            packageLabels,
            licenseId,
            qualityIssueType,
          ),
        ),
      );
      dispatch(refetchSummaries());
    };

/** Creates issue exceptions for the currently selected issues. */
export const createIssueExceptionsForSelectedIssues = withTargetFromSelection(createIssueExceptions);

const getUnlinkAction = _.constant<UnlinkAction>({ type: 'unlink' });

export const unlinkIssues =
  (target: ActionTarget): IssuesThunkAction =>
    async (dispatch) => {
      await dispatch(updateIssuesThunk(target, getUnlinkAction()));
      await dispatch(refetchSummaries());
    };

export const unlinkSelectedIssues = withTargetFromSelection(unlinkIssues);

const getExportAction = (ticket: AnyExportRequest): ExportAction => ({
  type: 'export',
  ticket,
});

export const exportIssues =
  (target: ActionTarget, ticket: AnyExportRequest): IssuesThunkAction =>
    async (dispatch) => {
      await dispatch(updateIssuesThunk(target, getExportAction(ticket)));
      await dispatch(refetchSummaries());
    };

export const exportSelectedIssues = withTargetFromSelection(exportIssues);

/**
 * Fetches the issue details for the status indicators on the
 * IssueDetails page. Both for global and project issue details.
 */
const fetchIssueDetailsAction = (
  id: number,
  scope: Scope,
  category: Category,
): ReduxAction<IssuesResponse<typeof category>> => ({
  type: actionTypes.FETCH_ISSUE_DETAILS,
  payload: async () =>
    get<IssuesResponse<typeof category>>(`${ENDPOINTS.FETCH_ISSUES}/${id.toString()}`, {
      scope,
      category,
    }),
});

/**
 * Fetches the issue licenses for licensing issue drawer.
 */
export const fetchIssueLicensesAction = (revisionLocator: string): ReduxAction<RevisionLicensesResponse> => ({
  type: actionTypes.FETCH_ISSUE_LICENSES,
  payload: async () =>
    get<RevisionLicensesResponse>(`${ENDPOINTS.FETCH_ISSUE_LICENSES}/${nuc(revisionLocator)}/licenses`),
});

/**
 * Fetch a list of affected projects for the given issue.
 */
const fetchAffectedProjects = (
  id: number,
  scope: Scope,
  category: Category,
): ReduxAction<IssuesResponse<typeof category>> => ({
  type: actionTypes.FETCH_AFFECTED_PROJECTS,
  payload: async () =>
    get<IssuesResponse<typeof category>>(`${ENDPOINTS.FETCH_ISSUES}/${id.toString()}/affected-projects`, {
      scope,
      category,
    }),
});

/**
 * Thunk action for fetching issue details,
 * which allows us to grab the scope and pass it along.
 */
export const fetchIssueDetails =
  (id: number): IssuesThunkAction =>
    (dispatch, getState) => {
      const state = getState();
      const scope = getScope(state);
      const category = getIssueCategory(state);
      dispatch(fetchAffectedProjects(id, scope, category));
      return dispatch(fetchIssueDetailsAction(id, scope, category));
    };

/**
 * Clears away the actual issueDetails, as well as the
 * affectedProjects and licenses.
 */
export const clearIssueDetails = () => ({
  type: actionTypes.CLEAR_ISSUE_DETAILS,
});

/**
 * After completing any Action (ie: ignore/unignore, export/unlink),
 * re-fetch the issue details so the drawer and the full-page details
 * both show the new state.
 */
export const refetchIssueDetailsAfterAction =
  (id: number): IssuesThunkAction =>
    (dispatch) => {
      dispatch(fetchIssueDetails(id));
    };

//  This action is used to re-fetch the summaries after taking an action on issues,
//  since the counts may be updated whenever an action takes place. Invalidating the
//  React Query category summary here keeps every count-changing thunk consistent —
//  callers only need to dispatch `refetchSummaries` and both the Redux summaries and
//  the React Query badge in the project subnav refresh.
export const refetchSummaries = (): IssuesThunkAction => (dispatch, getState) => {
  const state = getState();
  const scope = getScope(state);
  const teamId = TeamsModule.selectors.getTeamSelection(state);

  dispatch(fetchCategorySummary(scope, teamId));
  dispatch(fetchStatusSummary());
  dispatch(fetchIssues());
  dispatch(clearSelection());
  queryClient.invalidateQueries({ queryKey: ISSUE_CATEGORY_SUMMARY_QUERY_KEY_PREFIX });
};

export type FetchIssueExceptionPayload = {
  filters: IssueExceptionsFilter;
  pagination: IssueExceptionsPagination;
  search?: string;
  sortBy?: IssueExceptionSort;
  orderBy?: IssueExceptionOrderBy;
};

// This action fetches issue exceptions, which an optional policyId parameter
export const fetchIssueExceptions = ({
  filters,
  pagination,
  search,
  sortBy,
  orderBy,
}: FetchIssueExceptionPayload) => ({
  type: actionTypes.FETCH_ISSUE_EXCEPTIONS,
  payload: async () =>
    get<IssueExceptionsForDashboard>(ENDPOINTS.EXCEPTIONS_LIST, {
      filters,
      page: pagination.page,
      count: pagination.count,
      search,
      sortBy,
      orderBy,
    }),
});

export const removeIssueExceptions =
  (
    removeCriteria: IssueExceptionsRemoveRequest,
  ): IssuesThunkAction => async (dispatch) => {
    const removeResponseData = await dispatch(
      removeIssueExceptionsAction(removeCriteria),
    ) as unknown as AwaitedThunkResponse<{ count: number }>;

    // Refetch issue counts as the number of active issue is directly affected by removing exceptions
    await dispatch(refetchSummaries());

    const removeCount = removeResponseData?.value?.data?.count ?? 0;

    return removeCount;
  };

// This action deletes issue exceptions
export const removeIssueExceptionsAction = (removeCriteria: IssueExceptionsRemoveRequest) => ({
  type: actionTypes.REMOVE_ISSUE_EXCEPTIONS,
  payload: async () =>
    remove<IssueExceptionsRemoveRequest, IssueExceptionsRemoveResponse>(
      ENDPOINTS.EXCEPTIONS_LIST,
      {
        body: removeCriteria,
        json: true,
      },
    ),
});

export const extendIssueException =
  (exceptionId: number, expiresAfter: string | null): IssuesThunkAction =>
    async (dispatch) => {
      (await dispatch(extendIssueExceptionAction(exceptionId, expiresAfter))) as unknown as AwaitedThunkResponse;

      // Refetch issue counts as the number of active issue is directly affected by removing exceptions
      await dispatch(refetchSummaries());
    };

const extendIssueExceptionAction = (exceptionId: number, expiresAfter: string | null) => ({
  type: actionTypes.EXTEND_ISSUE_EXCEPTION,
  payload: async () =>
    put<{ expiresAfter: string | null }, void>(`${ENDPOINTS.EXCEPTIONS_LIST}/${exceptionId}`, {
      expiresAfter,
    }),
});

export const fetchIssuesForRevision =
  (revisionId: string): IssuesThunkAction =>
    async (dispatch, getState) => {
      const state = getState();
      const category = getIssueCategory(state);
      // Casting because IssuesQuery includes some non-overlapping types which mess up typechecking.
      const issueQuery = getFetchIssuesQuery(state) as IssuesQuery<typeof category>;

      const revision = (getRevisions(state).value || ([] as Array<IssueRevisionWithIssues<unknown[]>>)).find(
        (r) => r.revisionId === revisionId,
      );
      if (!revision) {
        return;
      }

      // Don't fetch more issues if they have all already been fetched
      if (revision.issueCount === revision.issues.length) {
        return;
      }

      const query = produce(issueQuery, (draftQuery) => {
        if (draftQuery.filter) {
          draftQuery.filter.revisionIds = [revisionId];
        } else {
          draftQuery.filter = { revisionIds: [revisionId] };
        }
        // specify secondary issue sorting order based on the current category
        if (category === 'vulnerability') {
          draftQuery.sort = SEVERITY_SORT.DESC;
        } else {
          draftQuery.sort = ISSUE_TYPE_SORT;
        }
        draftQuery.page = revision.nextPage.value ?? 1;
        draftQuery.count = 10;
      }) as APIIssuesQuery<typeof category>; // Cast back now that modifications are done.

      await dispatch(fetchRevisionIssuesAction(query));
    };

//  Action for posting a vuln dispute to the backend.
const postVulnDisputeAction = ({
  comment,
  reason,
  issueId,
  releaseGroupId,
}: VulnDisputeRequest & {
  issueId: number;
  releaseGroupId?: number;
}) => ({
  type: actionTypes.POST_VULN_DISPUTE,
  payload: async () =>
    post<VulnDisputeRequest>(
      `/api/issues/${issueId}/dispute`,
      {
        comment,
        reason,
      },
      releaseGroupId ? { releaseGroupId } : {},
    ),
});

/**
 * Thunk which reads the issueId and releaseGroupId from the
 * store and displays
 *
 * @param {string} comment - Comment for disputing the vulnerability.
 * @param {CveDataDisputeReasonsType} reason - A reason type.
 */
export const postVulnDispute =
  (comment: string, reason: CveDataDisputeReasonsType): ThunkAction<void, ReduxStore, never, AnyAction> =>
    (dispatch, getState) => {
      const state = getState();
      const details = getIssueDetails(state);

      if (details) {
        const issueId = details.id;
        const scope = getScope(state);
        const releaseGroupId = scope.type === 'releaseGroup' ? scope.id : undefined;

        return dispatch(
          postVulnDisputeAction({
            comment,
            reason,
            issueId,
            releaseGroupId,
          }),
        );
      }
      //  Always return from all code paths.
      return null;
    };

/**
 * Fetches the comments for a particular issue. Comments are scoped to
 * the source `projectId`, which comes from `issueDetails`, so we need to
 * wait until `issueDetails` is fetched before we dispatch this action.
 */
export const fetchIssueDetailsComments =
  (): ThunkAction<void, ReduxStore, never, AnyAction> => (dispatch, getState) => {
    //  Now that the state is updated we can get the active issue from the state.
    const state = getState();
    const projectId = getIssueCommentsProjectId(state);

    if (projectId) {
      dispatch(CommentActions.fetchAll(projectId));
    }
  };

/**
 * This is similar to the above action, but the issue details
 * page lacks some things which exist on the issues inbox page.
 *
 * Firstly, this one does not set the active issue ID, because
 * that is only used for determining if the issue drawer is open.
 *
 * Next, this fetches the user's org if it's not in Redux already,
 * as this is required for the export to Jira modal.
 *
 * The next big difference here is that we `await` the `fetchIssueDetails`
 * request, so that `issueDetails` will exist BEFORE we fetch the comments.
 * This isn't required for the issue drawer, because the data will already
 * be loaded from the index, so we can just pull the comment projectId
 * directly from the store.
 */
export const fetchIssueDetailsForFullPage =
  (issueId: number): ThunkAction<void, ReduxStore, never, AnyAction> =>
    async (dispatch, getState) => {
    //  Fetch the org only if it's not already in the store.
      const currentOrg = OrganizationsStore.selectors.getCurrentOrganization(getState());
      if (!currentOrg) {
        dispatch(OrganizationsStore.actions.fetchOne(window.user?.organizationId as number));
      }
      //  Fetch additional data for the issue details.
      //  Await the response so that getIssueCommentsProjectId will actually pull the correct ID
      await dispatch(fetchIssueDetails(issueId));
      //  Get the comments.
      dispatch(fetchIssueDetailsComments());
    };

/**
 * Takes the current `issueDetails.id` from the state and re-fetches the issueDetails using that.
 * Noop is there is no issueDetails?.id.
 */
export const refetchIssueDetails =
  (): ThunkAction<void, ReduxStore, never, AnyAction> => async (dispatch, getState) => {
    const issueDetails = getIssueDetails(getState());
    if (issueDetails?.id) {
      dispatch(fetchIssueDetails(issueDetails?.id));
    }
  };

/**
 * Expands a single revision on the current page.  If the revision has no issues loaded then the first page of issues will be fetched.
 */
export const expandIssueRevision =
  (revisionId: string): IssuesThunkAction =>
    async (dispatch, getState) => {
      const state = getState();
      const revision = (getRevisions(state).value || ([] as Array<IssueRevisionWithIssues<unknown[]>>)).find(
        (r) => r.revisionId === revisionId,
      );
      if (!revision) {
        return;
      }

      dispatch({
        type: actionTypes.EXPAND_ISSUE_REVISION,
        revisionId,
      });

      // Fetch issues if they haven't been fetched yet
      if (revision.issues.length === 0 && !revision.nextPage.isLoading) {
        await dispatch(fetchIssuesForRevision(revisionId));
      }
    };

/**
 * Expands all revisions on the current page.  Issues will be fetched for any
 * revision that doesn't have at least one page of issues loaded.  The revisions
 * will be fetched with some parallelism, but not too much to avoid overloading
 * the server.
 */
export const expandAllRevisions = (): IssuesThunkAction => async (dispatch, getState) => {
  const state = getState();
  if (getGrouping(state) !== 'revision') {
    return;
  }

  dispatch({
    type: actionTypes.EXPAND_ALL_ISSUE_REVISIONS,
  });

  // Only fetch revisions that don't have at least one page of issues already.
  const revisions = (getRevisions(state).value ?? ([] as Array<IssueRevisionWithIssues<unknown[]>>)).filter(
    (r) => r.issues.length === 0 && !r.nextPage.isLoading,
  );

  await promises.map(
    revisions,
    (revision) => dispatch(fetchIssuesForRevision(revision.revisionId)),
    // The concurrency number here is somewhat arbitrary.  3 seems to load all
    // the issues relatively quickly while not overloading the server with 20+
    // requests at once.
    { concurrency: 3 },
  );
};

/**
 * Collapses a single revision on the current page.
 */
export const collapseIssueRevision = (revisionId: string) => ({
  type: actionTypes.COLLAPSE_ISSUE_REVISION,
  revisionId,
});

/** Collapses all revisions on the current page. */
export const collapseAllIssueRevisions = () => ({
  type: actionTypes.COLLAPSE_ALL_ISSUE_REVISIONS,
});

/**
 * Fetches a summary of issue counts by issue type (ie: 'policy_conflict', 'vulnerability', etc.).
 * This is used for the project and release group summary page.
 */
export const fetchIssueTypeSummary = (scope: Scope): ReduxAction<IssueTypesSummary> => ({
  type: actionTypes.FETCH_ISSUE_TYPE_SUMMARY,
  payload: async () => get(ENDPOINTS.TYPES_SUMMARY, { scope }),
});

/**
 * Sets the found-after filter.
 */
export const setFoundAfter = (foundAfter: FoundAfterOption) => ({
  type: actionTypes.SET_FOUND_AFTER_FILTER,
  foundAfter,
});

/**
 * Action for fetching the unique package managers associated with the current issues.
 */
export const fetchPackageManagers = (
  category: Category,
  status: Status,
  scope: Scope,
): ReduxAction<IssuePackageManagersResponse> => ({
  type: actionTypes.FETCH_PACKAGE_MANAGERS,
  payload: async () => get(ENDPOINTS.PACKAGE_MANAGERS, { category, status, scope }),
});

/**
 * Action for fetching the unique CWEs associated with the current issues.
 */
export const fetchCwes = (
  category: Category,
  status: Status,
  scope: Scope,
): ReduxAction<string[]> => ({
  type: actionTypes.FETCH_CWES,
  payload: async () => get<string[]>(ENDPOINTS.CWES, { category, status, scope }),
});

/**
 * Sets the current package managers filter.
 */
export const setPackageManagersFilter = (packageManagers: string[], skipResetIssues?: boolean) => ({
  type: actionTypes.SET_PACKAGE_MANAGERS_FILTER,
  packageManagers,
  skipResetIssues,
});

export const setCwesFilter = (cwes: string[]) => ({
  type: actionTypes.SET_CWES_FILTER,
  cwes,
});

/**
 * Sets the current project labels filter.
 */
export const setProjectLabelsFilter = (projectLabels: string[]) => ({
  type: actionTypes.SET_PROJECT_LABELS_FILTER,
  projectLabels,
});

export const enterComparisonMode = () => ({
  type: actionTypes.ENTER_COMPARISON_MODE,
});

export const exitComparisonMode = () => ({
  type: actionTypes.EXIT_COMPARISON_MODE,
});

export const setComparisonRevision = (comparisonRevision: string) => ({
  type: actionTypes.SET_COMPARISON_REVISION,
  comparisonRevision,
});

export const setComparisonRelease = (comparisonRelease: number) => ({
  type: actionTypes.SET_COMPARISON_RELEASE,
  comparisonRelease,
});

export const setComparisonChangeStatus = (comparisonChangeStatus: ChangeStatus) => ({
  type: actionTypes.SET_COMPARISON_CHANGE_STATUS,
  comparisonChangeStatus,
});
